import {
    adjustBrightness,
    getContrastColor,
    calculateDimmedFont,
    calculateDimmedIcon,
    calculateDisabledBg,
    calculateAgentText,
    calculateDisabledButtonBg,
    calculateFontLink,
    calculateFontHoverGreen,
    calculateFontHoverGrey,
    calculateConstantLightGrey,
    calculateFontHoverLightGrey,
    calculateInactiveOpacity,
    calculateDisassociatedMemberOpacity,
    calculatePauseDark,
    calculateDropdownBg,
    calculateImplicitSpinnerBg,
    rgbaToHsl,
} from "../Utilities/Colour";

export const STANDARD_THEME = "standard_theme";
export const LIGHT_THEME = "light_theme";
export const DARK_THEME = "dark_theme";
export const NIGHT_OPS_THEME = "night_ops_theme";
export const PRETTY_THEME = "pretty_theme";
export const MONOCHROME_THEME = "monochrome_theme";

const THEMES = {
    [STANDARD_THEME]: {
        primary: "rgba(49, 56, 97, 1)",
        secondary: "rgba(110, 116, 197, 1)",
        fontLight: "rgba(255, 255, 255, 1)",
    },
    [DARK_THEME]: {
        primary: "rgba(49, 49, 50, 1)",
        secondary: "rgba(128, 88, 48, 1)",
        fontLight: "rgba(255, 255, 255, 1)",
    },
    [LIGHT_THEME]: {
        primary: "rgba(195, 195, 195, 1)",
        secondary: "rgba(144, 202, 249, 1)",
        fontLight: "rgba(0, 0, 0, 1)",
    },
    [MONOCHROME_THEME]: {
        primary: "rgba(216, 216, 216, 1)",
        secondary: "rgba(242, 242, 242, 1)",
        fontLight: "rgba(0, 0, 0, 1)",
    },
    [NIGHT_OPS_THEME]: {
        primary: "rgba(48, 57, 49, 1)",
        secondary: "rgba(139, 135, 74, 1)",
        fontLight: "rgba(255, 255, 255, 1)",
    },
    [PRETTY_THEME]: {
        primary: "rgba(88,62,92, 1)",
        secondary: "rgba(136,14,79, 1)",
        fontLight: "rgba(255, 255, 255, 1)",
    },
};

const DEFAULT_BRIGHTNESS_CONFIG = {
    accent: -15,
    page: 20,
    section: 20,
    primaryBorder: 15,
    sectionBorder: 5,
    darkSectionBorder: -20,
    icon: -30,
};

// Font dimming parameters - auto-calculated based on whether font is grayscale
// Grayscale fonts (Abs(R-G) < 10 AND Abs(R-B) < 10 AND Abs(G-B) < 10):
//   dim_factor=0.7, font_weight=1, bg_weight=1, dim_factor_icon=0.55
// Colored fonts + sec_bg closer to black (luminance < 0.5):
//   dim_factor=0.7, font_weight=0.9, bg_weight=0.6, dim_factor_icon=0.9
// Colored fonts + sec_bg closer to white (luminance >= 0.5):
//   dim_factor=0.9, font_weight=0.6, bg_weight=0.9, dim_factor_icon=0.6
// These are kept for backwards compatibility but null values trigger auto-calculation
const DEFAULT_DIM_FACTOR = null;
const DEFAULT_FONT_WEIGHT = null;
const DEFAULT_BG_WEIGHT = null;

// Icon dimming parameter - auto-calculated (null triggers auto-calculation)
const DEFAULT_DIM_FACTOR_ICON = null;

// Disabled button background color calculation parameters
// Controls lightness adjustments for disabled buttons based on their base lightness
const DEFAULT_DISABLED_BUTTON_PARAMS = {
    threshold1: 0.7, // Very light buttons (L > 0.7)
    adjustment1: 0.22, // Lighten by +0.22
    threshold2: 0.4, // Light buttons (L > 0.4)
    adjustment2: 0.2, // Lighten by +0.2
    threshold3: 0.18, // Dark buttons (L > 0.18)
    adjustment3: 0.17, // Lighten by +0.17
    adjustmentFinal: 0.1, // Very dark buttons: Lighten by +0.04
};

// Disabled element background color calculation parameters
// Controls lightness adjustments for disabled elements based on section background lightness
const DEFAULT_DISABLED_BG_PARAMS = {
    threshold1: 0.7, // Very light backgrounds (L > 0.7)
    adjustment1: -0.03, // Darken by -0.03
    threshold2: 0.5, // Light backgrounds (L > 0.5)
    adjustment2: -0.025, // Darken by -0.025
    threshold3: 0.2, // Dark backgrounds (L > 0.2)
    adjustment3: -0.02, // Darken by -0.02
    adjustmentFinal: 0.1, // Very dark backgrounds: Lighten by +0.1
};

const FIXED_COLORS = {
    success: "rgba(111, 197, 111, 1)",
    error: "rgba(176, 18, 22, 1)",
    info: "rgba(66, 133, 244, 1)",
    pause: "rgba(189, 189, 189, 1)",
    charcoalGrey: "rgba(54, 69, 79, 1)",
    darkGreen: "rgb(0, 128, 0, 1)",
    constantGrey: "rgba(128, 128, 128, 1)",
    mobileSuccess: "rgba(141, 198, 64, 1)",
};

function generateColorPalette(
    primaryColors,
    brightnessConfig = DEFAULT_BRIGHTNESS_CONFIG,
    dimFactor = null,
    fontWeight = null,
    bgWeight = null,
    dimFactorIcon = DEFAULT_DIM_FACTOR_ICON,
    disabledButtonParams = DEFAULT_DISABLED_BUTTON_PARAMS,
    disabledBgParams = DEFAULT_DISABLED_BG_PARAMS,
) {
    const { primary, secondary, fontLight } = primaryColors;

    const section = adjustBrightness(primary, brightnessConfig.section);
    const accent = adjustBrightness(primary, brightnessConfig.accent);
    const buttonBg = accent; // Button BG is set to Accent color
    const buttonBorder = accent; // Button Border is set to Accent color

    const sectionHsl = rgbaToHsl(section);
    const sectionBorderAdjustment = sectionHsl.l > 0.5 ? 5 : 2;

    const constantLightGrey = calculateConstantLightGrey(fontLight);

    const dropdownBg = calculateDropdownBg(accent);
    const dropdownBorder = dropdownBg;

    // Calculate implicit spinner background based on accent
    const implicitSpinnerBg = calculateImplicitSpinnerBg(accent);

    return {
        primary,
        secondary,
        fontLight,
        accent,
        page: adjustBrightness(primary, brightnessConfig.page),
        darkSectionBorder: adjustBrightness(
            primary,
            brightnessConfig.darkSectionBorder,
        ),
        section,
        primaryBorder: adjustBrightness(
            primary,
            brightnessConfig.primaryBorder,
        ),
        sectionBorder: adjustBrightness(primary, sectionBorderAdjustment),
        fontDark: getContrastColor(fontLight),
        fontDimmed: calculateDimmedFont(
            fontLight,
            section,
            dimFactor,
            fontWeight,
            bgWeight,
        ),
        fontLink: calculateFontLink(section),
        agentText: calculateAgentText(fontLight, section),
        icon: calculateDimmedIcon(fontLight, section, dimFactorIcon),
        disabledBg: calculateDisabledBg(section, disabledBgParams),
        buttonBg,
        buttonBorder,
        disabledButtonBg: calculateDisabledButtonBg(
            buttonBg,
            disabledButtonParams,
        ),
        disabledButtonBorder: calculateDisabledButtonBg(
            buttonBorder,
            disabledButtonParams,
        ),
        ...FIXED_COLORS,
        pauseDark: calculatePauseDark(primary),
        constantLightGrey,
        inactiveOpacity: calculateInactiveOpacity(section),
        disassociatedMemberOpacity:
            calculateDisassociatedMemberOpacity(section),
        fontHoverGreen: calculateFontHoverGreen(
            secondary,
            FIXED_COLORS.success,
        ),
        fontHoverGrey: calculateFontHoverGrey(secondary, FIXED_COLORS.pause),
        fontHoverLightGrey: calculateFontHoverLightGrey(
            section,
            constantLightGrey,
        ),
        dropdownBg,
        dropdownBorder,
        implicitSpinnerBg,
    };
}

function getColourSet(themeName) {
    if (!themeName || typeof themeName !== "string") return;
    const theme = THEMES[themeName];
    if (!theme) return null;
    return theme;
}

function setColorsToStorage(fullPalette) {
    localStorage.setItem("kaamfu_colors", JSON.stringify(fullPalette));
}

function setDimmingParamsToStorage(dimmingParams) {
    localStorage.setItem(
        "kaamfu_dimming_params",
        JSON.stringify(dimmingParams),
    );
}

function getDimmingParamsFromStorage() {
    try {
        const stored = localStorage.getItem("kaamfu_dimming_params");
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.warn("Failed to parse stored dimming params:", e);
    }
    return null;
}

function getColorsFromStorage() {
    try {
        const stored = localStorage.getItem("kaamfu_colors");
        if (stored) {
            return generateColorPalette(JSON.parse(stored));
        }
    } catch (e) {
        // Failed to parse stored colors
    }
    const colourSet = getColourSet(MONOCHROME_THEME);
    return generateColorPalette(colourSet);
}

export {
    THEMES,
    DEFAULT_BRIGHTNESS_CONFIG,
    DEFAULT_DIM_FACTOR,
    DEFAULT_FONT_WEIGHT,
    DEFAULT_BG_WEIGHT,
    DEFAULT_DIM_FACTOR_ICON,
    DEFAULT_DISABLED_BUTTON_PARAMS,
    DEFAULT_DISABLED_BG_PARAMS,
    FIXED_COLORS,
    setColorsToStorage,
    setDimmingParamsToStorage,
    getDimmingParamsFromStorage,
    generateColorPalette,
    getColourSet,
    getColorsFromStorage,
};
